import React, { useState, useEffect, useRef } from 'react';
import { useChat, useLocalParticipant } from '@livekit/components-react';
import '../styles/customChat.css';

export default function CustomChat({ isOpen, onClose, docked = false }) {
  const { chatMessages, send, isSending } = useChat();
  const { localParticipant } = useLocalParticipant();
  const [message, setMessage] = useState('');
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Keep scroll inside the messages list — never scrollIntoView (that
  // animates parent drawers / the page when chat opens on small screens).
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [chatMessages]);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return undefined;
    // When docked in the side rail, wait for the drawer slide to finish
    // so focus doesn't interrupt the open animation.
    const delay = docked ? 320 : 80;
    const timer = window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isOpen, docked]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;

    const messageToSend = message.trim();
    setMessage('');

    try {
      await send(messageToSend);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const localIdentity = localParticipant?.identity || 'You';

  // Stay mounted when docked+closed so useChat history is not wiped.
  const hiddenDocked = docked && !isOpen;

  return (
    <>
      {!docked && isOpen ? (
        <div
          className="custom-chat-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      ) : null}

      <div
        className={`custom-chat-container${isOpen ? ' open' : ''}${
          docked ? ' custom-chat-container--docked' : ''
        }${hiddenDocked ? ' is-hidden' : ''}`}
        aria-hidden={hiddenDocked}
      >
        <div className="custom-chat-header">
          <h3>Chat</h3>
          <button
            className="custom-chat-close-btn"
            onClick={onClose}
            aria-label="Close chat"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="custom-chat-messages" ref={messagesContainerRef}>
          {chatMessages.length === 0 ? (
            <div className="custom-chat-empty">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            chatMessages.map((msg) => {
              const isLocal = msg.from?.identity === localIdentity;
              const senderName = isLocal
                ? 'You'
                : msg.from?.name || msg.from?.identity || 'Unknown';

              return (
                <div
                  key={msg.timestamp}
                  className={`custom-chat-message ${isLocal ? 'local' : 'remote'}`}
                >
                  <div className="custom-chat-message-header">
                    <span className="custom-chat-sender-name">{senderName}</span>
                    <span className="custom-chat-timestamp">
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="custom-chat-message-text">{msg.message}</div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="custom-chat-input-form" onSubmit={handleSend}>
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={isSending}
            className="custom-chat-input"
          />
          <button
            type="submit"
            disabled={!message.trim() || isSending}
            className="custom-chat-send-btn"
          >
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </>
  );
}
